import { CueNote } from "../types";

const norm = (s: string) => (s || "").toLowerCase().normalize("NFKC").replace(/\s+/g, " ").trim();

export type MergeReport = {
  added: number;
  updated: number;
  skipped: number;
  conflicts: number;
};

export function mergeNotes(base: CueNote[], incoming: CueNote[]): { merged: CueNote[]; report: MergeReport } {
  const byId = new Map(base.map(n => [n.id, n]));
  const byKey = new Map(base.map(n => [`${norm(n.title)}|${norm((n as any).board ?? "")}`, n]));

  let added = 0, updated = 0, skipped = 0, conflicts = 0;
  const out = [...base];

  for (const inc of incoming) {
    const idMatch = inc.id ? byId.get(inc.id) : undefined;
    const key = `${norm(inc.title)}|${norm((inc as any).board ?? "")}`;
    const keyMatch = byKey.get(key);

    // prefer id match
    if (idMatch) {
      // update selected fields; de-dupe triggers
      const merged: CueNote = {
        ...idMatch,
        title: inc.title ?? idMatch.title,
        body_markdown: inc.body_markdown ?? idMatch.body_markdown,
        triggers: dedupe([...idMatch.triggers, ...(inc.triggers ?? [])]),
      };
      const idx = out.findIndex(n => n.id === idMatch.id);
      out[idx] = merged;
      byId.set(merged.id, merged);
      byKey.set(key, merged);
      updated++;
      continue;
    }

    if (keyMatch) {
      // conflict: keep existing and append imported as new note with suffix
      const copy: CueNote = {
        ...inc,
        id: `${inc.id || crypto.randomUUID()}__import`,
        title: `${inc.title} (import)`,
        triggers: dedupe(inc.triggers || []),
      };
      out.push(copy);
      byId.set(copy.id, copy);
      byKey.set(`${norm(copy.title)}|${norm((copy as any).board ?? "")}`, copy);
      conflicts++;
      continue;
    }

    // brand new
    const fresh: CueNote = {
      ...inc,
      id: inc.id || crypto.randomUUID(),
      triggers: dedupe(inc.triggers || []),
    };
    out.push(fresh);
    byId.set(fresh.id, fresh);
    byKey.set(`${norm(fresh.title)}|${norm((fresh as any).board ?? "")}`, fresh);
    added++;
  }

  return { merged: out, report: { added, updated, skipped, conflicts } };
}

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of arr) {
    const k = norm(t);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}
